/** Breadcrumb separator for search result Location line (with spaces). Not "." or "·". */
export const SEARCH_RESULT_PATH_SEPARATOR = ' > ';

/**
 * Normalize search Location text: use " > " between segments, not "." or middle-dot.
 * Safe for typical version-like "v1.0" (digit after dot → left alone).
 */
export function formatSearchResultPathLabel(label) {
  if (label == null || label === '') return '';
  let s = String(label).trim();
  if (!s) return '';
  // Unicode middle dot / bullet (older mockups)
  s = s.replace(/\s*[\u00B7\u2022]\s*/g, SEARCH_RESULT_PATH_SEPARATOR);
  // Space-padded period: "a . b"
  s = s.replace(/\s+\.\s+/g, SEARCH_RESULT_PATH_SEPARATOR);
  // Unspaced period before a letter only (legacy "Root.Folder.Child" join) — avoids "v1.0"
  if (!s.includes('>')) {
    s = s.replace(/\.(?=[A-Za-z])/g, SEARCH_RESULT_PATH_SEPARATOR);
  }
  s = s.replace(/\s*>\s*/g, ' > ');
  return s.replace(/\s{2,}/g, ' ').trim();
}

function getDashboardId(d) {
  return d.dashboardId || d.DashboardId;
}

function getDashboardName(d) {
  return d.dashboardName || d.DashboardName || d.name || 'Untitled';
}

function findFolderNode(nodes, folderId) {
  const id = folderId != null ? String(folderId) : '';
  for (const n of nodes || []) {
    if (String(n.FolderId) === id) return n;
    const sub = findFolderNode(n.children, folderId);
    if (sub) return sub;
  }
  return null;
}

/**
 * Flat index limited to `selectedFolderId` subtree (or full tree at root).
 * One row per folder and per dashboard placement under that scope.
 * @param {string} [rootLabel='Dashboard'] — first segment in path labels at tenant root (e.g. All Dashboards / My Dashboard).
 */
export function buildScopedMyDashboardsSearchIndex(
  explorerData,
  selectedFolderId,
  folderById,
  rootLabel = 'Dashboard'
) {
  if (!explorerData) return [];

  const rows = [];

  const walk = (nodes, ancestorNames) => {
    for (const node of nodes || []) {
      const fname = node.FolderName || '';
      const pathHere = formatSearchResultPathLabel(
        [rootLabel, ...ancestorNames, fname].filter(Boolean).join(SEARCH_RESULT_PATH_SEPARATOR)
      );
      rows.push({
        kind: 'folder',
        id: `folder-${node.FolderId}`,
        folderItem: node,
        displayName: fname,
        pathLabel: pathHere,
      });
      for (const d of node.dashboards || []) {
        const did = getDashboardId(d);
        rows.push({
          kind: 'dashboard',
          id: `dash-${did}-${node.FolderId}`,
          displayName: getDashboardName(d),
          pathLabel: pathHere,
          dashboardId: did,
          dashboardItem: d,
        });
      }
      walk(node.children || [], [...ancestorNames, fname]);
    }
  };

  if (!selectedFolderId) {
    walk(explorerData.folders || [], []);
    for (const d of explorerData.rootDashboards || []) {
      const did = getDashboardId(d);
      rows.push({
        kind: 'dashboard',
        id: `dash-${did}-root`,
        displayName: getDashboardName(d),
        pathLabel: formatSearchResultPathLabel(rootLabel),
        dashboardId: did,
        dashboardItem: d,
      });
    }
    return rows;
  }

  const node = findFolderNode(explorerData.folders || [], selectedFolderId);
  if (!node) return [];

  const pathToSelected = [];
  let cur = folderById.get(selectedFolderId);
  const seen = new Set();
  while (cur && !seen.has(cur.FolderId)) {
    pathToSelected.push(cur.FolderName || 'Folder');
    seen.add(cur.FolderId);
    cur = cur.ParentFolderId ? folderById.get(cur.ParentFolderId) : null;
  }
  pathToSelected.reverse();
  const prefix = pathToSelected.slice(0, -1);

  walk([node], prefix);
  return rows;
}

/** @param {'all'|'folder'|'dashboard'} kind */
export function filterMyDashboardsSearch(rows, query, kind = 'all') {
  const q = (query || '').trim().toLowerCase();
  let base = rows;
  if (kind === 'folder') base = rows.filter((r) => r.kind === 'folder');
  else if (kind === 'dashboard') base = rows.filter((r) => r.kind === 'dashboard');
  if (!q) return [];
  return base.filter((r) => (r.displayName || '').toLowerCase().includes(q));
}
